import { zodResolver } from '@hookform/resolvers/zod';
import AssignmentReturnRounded from '@mui/icons-material/AssignmentReturnRounded';
import DoneAllRounded from '@mui/icons-material/DoneAllRounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { businessDate, money, qty as fmtQty } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { fromUnits, toUnits } from '../payments/allocation';
import { idempotency, invalidateMoney } from '../payments/kit/hooks';
import { links, todayIn } from '../payments/kit/labels';
import { BackLink, DetailSkeleton, ErrorState, SectionCard } from '../payments/kit/ui';
import { useNotesFor, useSalesInvoice } from './api';
import {
  baseToLineUnits,
  checkDraft,
  convertUnits,
  estimateLineCredit,
  returnable,
  unitsToQty,
  usageByLine,
} from './calc';
import {
  FormGrid,
  GhostButton,
  HeaderFields,
  LineInput,
  PickSourceCard,
  SummaryPanel,
} from './kit';
import { returnHeaderSchema, type ReturnHeaderValues } from './schemas';
import { UnitPick, type PickedUnit } from './UnitPick';

type Condition = 'RESALABLE' | 'DAMAGED';
interface LineState {
  qty: string;
  condition: Condition;
  writeOff: boolean;
  /** Returned in another unit than the invoice line's. */
  unit?: PickedUnit;
}

/** Return goods against a posted invoice: quantities per line, condition, then a credit note. */
export function SalesReturnFormPage() {
  const [params, setParams] = useSearchParams();
  const invoiceId = params.get('invoiceId');
  if (!invoiceId)
    return (
      <>
        <BackLink to="/returns" label="Returns & notes" />
        <PageHeader title="New sales return" />
        <PickSourceCard
          kind="salesInvoice"
          title="Which invoice is being returned?"
          body="Goods come back into stock at their original cost and the customer gets a credit note for them."
          onPick={(o) => setParams({ invoiceId: o.id })}
        />
      </>
    );
  return <SalesReturnForm invoiceId={invoiceId} />;
}

function SalesReturnForm({ invoiceId }: { invoiceId: string }) {
  const { format } = useShop();
  const d = format.currencyDecimals;
  const { can } = useAuth();
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const invoice = useSalesInvoice(invoiceId);
  const prior = useNotesFor('credit', invoiceId);
  const usage = useMemo(() => usageByLine(prior.notes), [prior.notes]);
  const [lines, setLines] = useState<Record<string, LineState>>({});
  const [pending, setPending] = useState<ReturnHeaderValues | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ReturnHeaderValues>({
    resolver: zodResolver(returnHeaderSchema),
    defaultValues: { docDate: todayIn(format.timeZone), reason: '', notes: '' },
  });

  const rows = useMemo(
    () =>
      (invoice.data?.lines ?? []).map((l) => {
        const u = usage.get(l.id);
        const returnedBase = u?.returnedBase ?? 0n;
        const state = lines[l.id] ?? { qty: '', condition: 'RESALABLE', writeOff: true };
        const unit = state.unit ?? { unitId: l.unitId, symbol: l.unitSymbol, factor: l.factor };
        const left = returnable(l.qty, l.factor, returnedBase);
        // In another unit: what is left and the credit go through the line unit (base units).
        const inLineUnit = state.unit
          ? unitsToQty(convertUnits(toUnits(state.qty) ?? 0n, unit.factor, l.factor))
          : state.qty;
        return {
          line: l,
          state,
          unit,
          returned: baseToLineUnits(returnedBase, l.factor),
          max: state.unit ? convertUnits(left, l.factor, unit.factor) : left,
          credit: estimateLineCredit(l.lineTotal, l.qty, inLineUnit, d),
        };
      }),
    [invoice.data, usage, lines, d],
  );
  const check = checkDraft(
    rows.map((r) => ({ lineId: r.line.id, max: r.max, value: r.state.qty })),
    {
      overMessage: 'More than can be returned',
    },
  );
  const credit = rows.reduce((s, r) => s + (check.errors[r.line.id] ? 0n : r.credit), 0n);
  const damaged = rows.filter(
    (r) => r.state.condition === 'DAMAGED' && (toUnits(r.state.qty) ?? 0n) > 0n,
  );

  const patch = (id: string, p: Partial<LineState>) =>
    setLines((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? { qty: '', condition: 'RESALABLE', writeOff: true }), ...p },
    }));

  const save = useMutation({
    mutationFn: (v: ReturnHeaderValues) =>
      unwrap(
        api.POST('/sales-returns', {
          params: idempotency(),
          body: {
            salesInvoiceId: invoiceId,
            docDate: v.docDate,
            reason: v.reason || null,
            notes: v.notes || null,
            lines: rows
              .filter((r) => (toUnits(r.state.qty) ?? 0n) > 0n)
              .map((r) => ({
                invoiceLineId: r.line.id,
                qty: r.state.qty.trim(),
                ...(r.state.unit ? { unitId: r.state.unit.unitId } : {}),
                condition: r.state.condition,
                writeOff: r.state.condition === 'DAMAGED' ? r.state.writeOff : true,
              })),
          },
        }),
      ),
    onSuccess: async (r) => {
      notify.success(
        r.creditNote
          ? `${r.number} posted — credit note ${r.creditNote.number} for ${money(r.creditNote.total, format)}`
          : `${r.number} posted`,
      );
      await invalidateMoney(qc);
      void navigate(links.salesReturn(r.id));
    },
    onError: (e) => {
      if (e instanceof ApiError)
        for (const det of e.details) {
          const field = det.path.split('.').pop();
          if (field === 'docDate' || field === 'reason' || field === 'notes')
            setError(field, { message: det.message });
        }
      notify.error(e);
    },
  });

  if (invoice.isLoading || prior.loading) return <DetailSkeleton />;
  if (invoice.isError || !invoice.data || prior.error)
    return (
      <>
        <BackLink to="/returns" label="Returns & notes" />
        <ErrorState error={invoice.error ?? prior.error} onRetry={() => void invoice.refetch()} />
      </>
    );

  const inv = invoice.data;
  const posted = inv.status === 'POSTED';
  const direct = !inv.deliveryId && !inv.salesOrderId;
  const nothingLeft = rows.every((r) => r.max === 0n);
  const allowed = can('sales.return');

  return (
    <>
      <BackLink to="/returns" label="Returns & notes" />
      <PageHeader
        title="New sales return"
        subtitle={
          <>
            Against{' '}
            <Link component={RouterLink} to={links.salesInvoice(inv.id)} sx={{ fontWeight: 650 }}>
              {inv.number}
            </Link>{' '}
            · {inv.contact.name} · {businessDate(inv.docDate, format)}
          </>
        }
        actions={
          <Button
            variant="outlined"
            onClick={() => void navigate('/returns/sales/new', { replace: true })}
          >
            Change invoice
          </Button>
        }
      />
      {!posted && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {inv.number} is {inv.status.toLowerCase()} — only posted invoices can be returned.
        </Alert>
      )}
      {!allowed && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          You don’t have permission to record returns.
        </Alert>
      )}
      <form
        noValidate
        onSubmit={(e) =>
          void handleSubmit((v) => {
            if (check.valid) setPending(v);
          })(e)
        }
      >
        <FormGrid
          main={
            <>
              {direct && (
                <Alert severity="info">
                  This invoice wasn’t delivered from stock in the app (a direct invoice), so the
                  return issues a credit only — no stock comes back.
                </Alert>
              )}
              <SectionCard
                flush
                title="What is coming back"
                subtitle={
                  nothingLeft
                    ? 'Everything on this invoice has already been returned.'
                    : 'Enter the quantity returned for each line'
                }
                action={
                  <GhostButton
                    onClick={() =>
                      setLines((prev) => {
                        const next = { ...prev };
                        for (const r of rows)
                          next[r.line.id] = {
                            ...(prev[r.line.id] ?? { condition: 'RESALABLE', writeOff: true }),
                            qty: r.max > 0n ? unitsToQty(r.max) : '',
                          };
                        return next;
                      })
                    }
                  >
                    <DoneAllRounded fontSize="small" sx={{ mr: 0.75 }} />
                    Return everything
                  </GhostButton>
                }
              >
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          Sold
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          Can return
                        </TableCell>
                        <TableCell align="right">Return</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          Condition
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          Credit
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow
                          key={r.line.id}
                          sx={{ opacity: r.max === 0n ? 0.55 : 1, verticalAlign: 'top' }}
                        >
                          <TableCell sx={{ pt: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {r.line.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {r.line.sku} · {money(r.line.unitPrice, format)} / {r.line.unitSymbol}
                              {r.returned > 0n &&
                                ` · ${fmtQty(fromUnits(r.returned, 0), format)} returned before`}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            className="tabular"
                            sx={{ display: { xs: 'none', md: 'table-cell' }, pt: 1.5 }}
                          >
                            {fmtQty(r.line.qty, format)} {r.line.unitSymbol}
                          </TableCell>
                          <TableCell
                            align="right"
                            className="tabular"
                            sx={{
                              display: { xs: 'none', sm: 'table-cell' },
                              pt: 1.5,
                              fontWeight: 600,
                            }}
                          >
                            {fmtQty(fromUnits(r.max, 0), format)}
                            {r.state.unit ? ` ${r.unit.symbol}` : ''}
                          </TableCell>
                          <TableCell align="right">
                            <LineInput
                              value={r.state.qty}
                              onChange={(v) => patch(r.line.id, { qty: v })}
                              error={check.errors[r.line.id]}
                              label={`Quantity of ${r.line.description} returned`}
                              suffix={r.unit.symbol}
                              width={120}
                            />
                            <UnitPick
                              line={r.line}
                              value={r.unit}
                              onChange={(unit) =>
                                patch(r.line.id, {
                                  qty: '',
                                  unit: unit.unitId === r.line.unitId ? undefined : unit,
                                })
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <TextField
                              select
                              size="small"
                              value={r.state.condition}
                              onChange={(e) =>
                                patch(r.line.id, { condition: e.target.value as Condition })
                              }
                              sx={{ minWidth: 132 }}
                              slotProps={{
                                htmlInput: { 'aria-label': `Condition of ${r.line.description}` },
                              }}
                            >
                              <MenuItem value="RESALABLE">Resalable</MenuItem>
                              <MenuItem value="DAMAGED">Damaged</MenuItem>
                            </TextField>
                            {r.state.condition === 'DAMAGED' && (
                              <FormControlLabel
                                sx={{ display: 'flex', mt: 0.5, mr: 0 }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={r.state.writeOff}
                                    onChange={(_, v) => patch(r.line.id, { writeOff: v })}
                                  />
                                }
                                label={<Typography variant="caption">Write off</Typography>}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            className="tabular"
                            sx={{
                              display: { xs: 'none', md: 'table-cell' },
                              pt: 1.5,
                              fontWeight: 600,
                            }}
                          >
                            {r.credit > 0n ? money(fromUnits(r.credit, d), format) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionCard>
              <SectionCard title="Details">
                <HeaderFields
                  register={register}
                  errors={errors}
                  reasonPlaceholder="e.g. wrong size, expired, customer changed mind"
                />
              </SectionCard>
            </>
          }
          side={
            <SummaryPanel
              title="Summary"
              rows={[
                {
                  label: 'Invoice',
                  value: (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <span>{money(inv.total, format)}</span>
                      <StatusChip status={inv.paymentStatus} />
                    </Stack>
                  ),
                },
                { label: 'Outstanding', value: money(inv.outstanding, format) },
                { label: 'Lines returned', value: String(check.count) },
                ...(damaged.length
                  ? [{ label: 'Damaged lines', value: String(damaged.length) }]
                  : []),
              ]}
              total={{ label: 'Credit (est.)', value: money(fromUnits(credit, d), format) }}
              explain={
                <>
                  A credit note is posted for the returned share of each line (with its tax). It
                  settles {inv.number} first;{' '}
                  {Number(inv.outstanding) > 0
                    ? 'anything above what is still owed becomes credit for the customer.'
                    : 'the invoice is paid, so it becomes credit you can refund or use later.'}
                  {damaged.length > 0 && ' Damaged goods go to the Damaged location.'}
                </>
              }
              actions={
                <>
                  <Button variant="outlined" onClick={() => void navigate('/returns')}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<AssignmentReturnRounded />}
                    disabled={!allowed || !posted || !check.valid}
                  >
                    Post return
                  </Button>
                </>
              }
            />
          }
        />
      </form>
      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title="Post this return?"
        confirmLabel="Post return"
        body={
          <Box component="span">
            {check.count} line{check.count === 1 ? '' : 's'} from {inv.number} and a credit note of
            about <b>{money(fromUnits(credit, d), format)}</b> for {inv.contact.name}. Returns are
            final — they can’t be edited or cancelled.
          </Box>
        }
        onConfirm={() =>
          pending ? save.mutateAsync(pending).catch(() => undefined) : Promise.resolve()
        }
      />
    </>
  );
}
